import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { get as getUser, isSuperuser } from 'js/lib/user';

import type { ContextProps } from 'bundles/enterprise-admin-home/components/ContextButton';
import type {
  PartnerMembershipsQuery_PartnerMembershipsV1Resource_findByUser_elements as PartnerId,
  PartnerMembershipsQuery as PartnerMembershipsQueryData,
  PartnerMembershipsQueryVariables,
} from 'bundles/enterprise-admin-home/components/__generated__/PartnerMembershipsQuery';
import AdminNavigationPartners from 'bundles/enterprise-admin-navigation/queries/AdminNavigationPartners.graphql';
import type {
  AdminNavigationPartnersQuery,
  AdminNavigationPartnersQueryVariables,
} from 'bundles/enterprise-admin-navigation/queries/__generated__/AdminNavigationPartners';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

const PartnerMembershipsQuery = gql`
  query PartnerMembershipsQuery($userId: String!) {
    PartnerMembershipsV1Resource {
      findByUser(userId: $userId, partnerRole: "PARTNER_ADMIN") {
        elements {
          id
          partnerId {
            maestroId
          }
        }
      }
    }
  }
`;

type OrgSwitcherDataReturn = {
  contexts: ContextProps[];
  isLoading: boolean;
};

/**
 * Fetches the dynamic org-context rows for the org-switcher popup
 * (Educator Admin, sibling orgs, current user's "My Coursera" entry).
 *
 * Static popup items (settings, help, logout) live declaratively in
 * `nav-config/useAdminOrgSwitcherItems` — NOT here.
 */
export const useOrgSwitcherData = (
  thirdPartyOrganization: AdminDashboardOrg,
  thirdPartyOrganizations: AdminDashboardOrg[],
  thirdPartySlug?: string
): OrgSwitcherDataReturn => {
  const { data: partnerMembershipsData, loading: partnerMembershipsLoading } = useQuery<
    PartnerMembershipsQueryData,
    PartnerMembershipsQueryVariables
  >(PartnerMembershipsQuery, {
    variables: { userId: `${getUser().id}` },
  });

  const nonNullPartnerIds = (partnerMembershipsData?.PartnerMembershipsV1Resource?.findByUser?.elements || []).filter(
    (el) => !!el
  ) as PartnerId[];
  const myPartnerIds = nonNullPartnerIds.map((el) => String(el.partnerId.maestroId));

  const { data: partnersData, loading: partnersLoading } = useQuery<
    AdminNavigationPartnersQuery,
    AdminNavigationPartnersQueryVariables
  >(AdminNavigationPartners, {
    skip: !myPartnerIds || myPartnerIds.length === 0,
    variables: { ids: myPartnerIds },
    context: { clientName: 'gatewayGql' },
  });

  const myPartners = useMemo(() => partnersData?.Partner?.queryByIds ?? [], [partnersData?.Partner?.queryByIds]);
  const isUserSuperuser = isSuperuser();
  const { photo_120: photoUrl, fullName } = getUser();

  const contexts = useMemo(() => {
    const result: ContextProps[] = [];

    if (isUserSuperuser) {
      result.push({
        key: 'superuser-admin',
        href: '/admin/',
        header: _t('Educator Admin'),
        label: _t('Educator Admin'),
        showLabel: false,
      });
    } else if (myPartners && myPartners.length > 0) {
      myPartners.forEach(({ name, squareLogo, rectangularLogo }) => {
        result.push({
          key: `partner-${name}`,
          href: '/admin/',
          header: _t('Educator Admin'),
          label: name,
          showLabel: true,
          photoUrl: squareLogo || rectangularLogo || undefined,
        });
      });
    }

    (thirdPartySlug
      ? thirdPartyOrganizations
      : thirdPartyOrganizations.filter((tpo) => tpo.id !== thirdPartyOrganization.id)
    ).forEach(({ name, slug, squareLogo, rectangularLogo, primaryColor, iconColor }) => {
      result.push({
        key: slug,
        href: `/o/${slug}/admin`,
        header: _t('Organization Admin'),
        label: name,
        showLabel: true,
        photoUrl: squareLogo || rectangularLogo || undefined,
        primaryColor: primaryColor || undefined,
        iconColor: iconColor || undefined,
      });
    });

    result.push({
      key: 'my-coursera',
      href: '/',
      header: _t('My Coursera'),
      label: fullName,
      showLabel: true,
      photoUrl,
    });

    return result;
  }, [
    isUserSuperuser,
    myPartners,
    thirdPartyOrganization.id,
    thirdPartyOrganizations,
    thirdPartySlug,
    photoUrl,
    fullName,
  ]);

  return {
    contexts,
    isLoading: partnerMembershipsLoading || partnersLoading,
  };
};
